import React from 'react';
import {
  Card,
  Typography,
  Space,
  Select,
  InputNumber,
  Switch,
  Button,
  Alert,
  Tag,
  Row,
  Col,
  Divider,
  Tooltip,
  Empty,
  message,
  Input,
  Skeleton,
  Modal,
  Checkbox,
  Table,
  Popconfirm
} from 'antd';
import {
  ReloadOutlined,
  LinkOutlined,
  MessageOutlined,
  PushpinOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  FilterOutlined
} from '@ant-design/icons';

// SLA — просмотр активных SLA и быстрые фильтры.
//
// Ожидаемый API (на бэке):
//   GET /api/sla?status=pending&star=2&limit=50
// Ответ:
//   { ok:true, items:[ ... ] }
// Где item (пример):
//   {
//     id, source_chat_id, incoming_message_id, incoming_ts,
//     incoming_from_id, incoming_from_name, incoming_from_username,
//     incoming_text, status, alerted_ts, answered_ts,
//     alert_chat_id, alert_message_id, progress_ts
//   }

function getInitData() {
  try {
    return window.Telegram?.WebApp?.initData || '';
  } catch (_) {
    return '';
  }
}

function useDebouncedValue(value, delayMs = 250) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), Math.max(0, Number(delayMs) || 0));
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function fmtTs(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts * 1000).toLocaleString();
  } catch (_) {
    return String(ts);
  }
}

function buildMsgLink(chatId, messageId) {
  if (!chatId || !messageId) return '';
  const s = String(chatId);
  if (s.startsWith('-100')) {
    const cid = s.replace('-100', '');
    return `https://t.me/c/${cid}/${messageId}`;
  }
  return '';
}

function buildChatLink(chatId) {
  if (!chatId) return '';
  const s = String(chatId);
  if (s.startsWith('-100')) {
    const cid = s.replace('-100', '');
    return `https://t.me/c/${cid}/1`;
  }
  return '';
}

function fmtDurationSec(totalSec) {
  const s = Math.max(0, Number(totalSec) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}ч ${m}м`;
  if (m > 0) return `${m}м ${sec}с`;
  return `${sec}с`;
}

function fmtAgo(ts) {
  if (!ts) return '—';
  const now = Math.floor(Date.now() / 1000);
  const d = now - Number(ts || 0);
  if (!Number.isFinite(d)) return '—';
  if (d < 10) return 'только что';
  if (d < 60) return `${d}с назад`;
  const m = Math.floor(d / 60);
  if (m < 60) return `${m}м назад`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}ч назад`;
  const days = Math.floor(h / 24);
  return `${days}д назад`;
}

function statusMeta(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'pending') return { label: 'ОЖИДАЕТ', emoji: '⏳', color: 'gold' };
  if (s === 'alerted') return { label: 'ПРОСРОЧЕНО', emoji: '⚠️', color: 'red' };
  if (s === 'in_progress') return { label: 'В РАБОТЕ', emoji: '🟠', color: 'orange' };
  if (s === 'answered') return { label: 'ЗАКРЫТО', emoji: '✅', color: 'green' };
  return { label: status ? String(status).toUpperCase() : '—', emoji: '•', color: 'default' };
}

async function fetchJson(path, opts = {}) {
  const method = (opts.method || 'GET').toUpperCase();
  const headers = {
    'x-telegram-init-data': getInitData(),
    ...(opts.headers || {})
  };

  let body = opts.body;
  if (body && typeof body === 'object' && !(body instanceof FormData)) {
    headers['content-type'] = headers['content-type'] || 'application/json';
    body = JSON.stringify(body);
  }

  const res = await fetch(path, { method, headers, body });
  const data = await res.json().catch(() => ({}));

  if (!res.ok || data?.ok === false) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

export default function Sla() {
  const [status, setStatus] = React.useState('pending');
  const [star, setStar] = React.useState('');
  const [limit, setLimit] = React.useState(50);

  const [autoRefresh, setAutoRefresh] = React.useState(true);
  const [refreshSec, setRefreshSec] = React.useState(30);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [items, setItems] = React.useState([]);

  const [q, setQ] = React.useState('');
  const qDebounced = useDebouncedValue(q, 250);

  // Selection
  const [selectedIds, setSelectedIds] = React.useState([]);

  // Cleanup modal
  const [cleanupOpen, setCleanupOpen] = React.useState(false);
  const [cleanupHours, setCleanupHours] = React.useState(24);
  const [cleanupAlsoEvents, setCleanupAlsoEvents] = React.useState(true);
  const [cleanupAlsoSeenAlerts, setCleanupAlsoSeenAlerts] = React.useState(true);
  const [cleanupStatus, setCleanupStatus] = React.useState('all');
  const [cleanupStar, setCleanupStar] = React.useState('all');

  const isAutoTickRef = React.useRef(false);

  const stats = React.useMemo(() => {
    const by = { pending: 0, alerted: 0, in_progress: 0, answered: 0, other: 0 };
    for (const it of items) {
      const s = String(it?.status || '').toLowerCase();
      if (s in by) by[s] += 1;
      else by.other += 1;
    }
    return { total: items.length, ...by };
  }, [items]);

  const filteredItems = React.useMemo(() => {
    const needle = String(qDebounced || '').trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => {
      const from = (it?.incoming_from_username ? `@${it.incoming_from_username}` : it?.incoming_from_name || it?.incoming_from_id || '').toString();
      const text = (it?.incoming_text || '').toString();
      const chat = (it?.source_chat_id || '').toString();
      const id = (it?.id || '').toString();
      return (
        text.toLowerCase().includes(needle) ||
        from.toLowerCase().includes(needle) ||
        chat.toLowerCase().includes(needle) ||
        id.toLowerCase().includes(needle)
      );
    });
  }, [items, qDebounced]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      const s = String(star || '').trim();
      if (s) params.set('star', s);
      const l = Number(limit);
      params.set('limit', String(Number.isFinite(l) && l > 0 ? l : 50));

      const data = await fetchJson(`/api/sla?${params.toString()}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setSelectedIds([]);

      if (!isAutoTickRef.current) message.success('SLA обновлены');
      isAutoTickRef.current = false;
    } catch (e) {
      const msg =
        e?.status === 401
          ? 'Не авторизовано: откройте мини-апп внутри Telegram'
          : (e?.message || 'Ошибка загрузки SLA');
      setItems([]);
      setError(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [status, star, limit]);

  const deleteSelected = React.useCallback(async () => {
    if (!selectedIds.length) {
      message.info('Ничего не выбрано');
      return;
    }
    Modal.confirm({
      title: `Удалить выбранные SLA (${selectedIds.length})?`,
      content: 'Действие необратимо. Удалятся записи из базы.',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      centered: true,
      onOk: async () => {
        try {
          await fetchJson('/api/sla/delete', { method: 'POST', body: { ids: selectedIds } });
          message.success('Удалено');
          setSelectedIds([]);
          isAutoTickRef.current = true;
          await load();
        } catch (e) {
          message.error(e?.message || 'Ошибка удаления');
        }
      }
    });
  }, [selectedIds, load]);

  const deleteOne = React.useCallback(async (id) => {
    await fetchJson('/api/sla/delete', { method: 'POST', body: { ids: [id] } });
  }, []);

  const deleteAlert = React.useCallback(async (slaId) => {
    await fetchJson('/api/sla/alert/delete', { method: 'POST', body: { sla_id: slaId } });
  }, []);

  const runCleanup = React.useCallback(async () => {
    try {
      const hours = Math.max(1, Math.min(720, Number(cleanupHours) || 24));
      const nowSec = Math.floor(Date.now() / 1000);
      const fromSec = nowSec - hours * 3600;
      const toSec = nowSec;

      const params = new URLSearchParams();
      params.set('from', String(fromSec));
      params.set('to', String(toSec));
      if (cleanupStatus !== 'all') params.set('status', cleanupStatus);
      if (cleanupStar !== 'all') params.set('star', cleanupStar);

      setLoading(true);

      let slaRes = null, eventsRes = null, seenRes = null;

      try {
        slaRes = await fetchJson(`/api/sla/cleanup?${params.toString()}`, { method: 'DELETE' });
      } catch (e) {
        slaRes = { ok: false, error: e?.message || 'Ошибка SLA' };
      }

      if (cleanupAlsoEvents) {
        try {
          const ev = new URLSearchParams();
          ev.set('from', String(fromSec));
          ev.set('to', String(toSec));
          eventsRes = await fetchJson(`/api/events/cleanup?${ev.toString()}`, { method: 'DELETE' });
        } catch (e) {
          eventsRes = { ok: false, error: e?.message || 'Ошибка событий' };
        }
      }

      if (cleanupAlsoSeenAlerts) {
        try {
          const sa = new URLSearchParams();
          sa.set('from', String(fromSec));
          sa.set('to', String(toSec));
          seenRes = await fetchJson(`/api/seen_alerts/cleanup?${sa.toString()}`, { method: 'DELETE' });
        } catch (e) {
          seenRes = { ok: false, error: e?.message || 'Ошибка seen_alerts' };
        }
      }

      const parts = [];
      if (slaRes?.count != null) parts.push(`SLA: ${slaRes.count}`);
      else parts.push(`SLA: ${slaRes?.ok ? '✓' : (slaRes?.error || 'ошибка')}`);

      if (cleanupAlsoEvents) {
        if (eventsRes?.count != null) parts.push(`события: ${eventsRes.count}`);
        else parts.push(`события: ${eventsRes?.ok ? '✓' : (eventsRes?.error || 'ошибка')}`);
      }

      if (cleanupAlsoSeenAlerts) {
        if (seenRes?.count != null) parts.push(`seen_alerts: ${seenRes.count}`);
        else parts.push(`seen_alerts: ${seenRes?.ok ? '✓' : (seenRes?.error || 'ошибка')}`);
      }

      message.success(`Очистка выполнена — ${parts.join(', ')}`);
      setCleanupOpen(false);

      isAutoTickRef.current = true;
      await load();
    } catch (e) {
      message.error(e?.message || 'Ошибка очистки');
    } finally {
      setLoading(false);
    }
  }, [
    cleanupHours,
    cleanupAlsoEvents,
    cleanupAlsoSeenAlerts,
    cleanupStatus,
    cleanupStar,
    load
  ]);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (!autoRefresh) return;
    const sec = Math.max(10, Math.min(300, Number(refreshSec) || 30));
    const t = setInterval(() => {
      isAutoTickRef.current = true;
      load();
    }, sec * 1000);
    return () => clearInterval(t);
  }, [autoRefresh, refreshSec, load]);

  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys) => setSelectedIds(keys)
  };

  const columns = React.useMemo(() => {
    return [
      {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (_, it) => {
          const meta = statusMeta(it.status);
          return (
            <Tag color={meta.color} style={{ borderRadius: 999, margin: 0 }}>
              {meta.emoji} {meta.label}
            </Tag>
          );
        }
      },
      {
        title: 'SLA_ID',
        dataIndex: 'id',
        key: 'id',
        width: 95,
        render: (v) => <Typography.Text code>{v}</Typography.Text>,
        sorter: (a, b) => Number(a?.id || 0) - Number(b?.id || 0),
        defaultSortOrder: 'descend'
      },
      {
        title: 'Ожидание',
        key: 'wait',
        width: 120,
        render: (_, it) => {
          const nowSec = Math.floor(Date.now() / 1000);
          const waitSec = (it.answered_ts || it.progress_ts || nowSec) - (it.incoming_ts || nowSec);
          return <Typography.Text strong>{fmtDurationSec(waitSec)}</Typography.Text>;
        }
      },
      {
        title: 'Чат',
        dataIndex: 'source_chat_id',
        key: 'source_chat_id',
        width: 180,
        render: (v, it) => {
          const chatRef = buildChatLink(it.source_chat_id);
          return (
            <Space size={8}>
              <Typography.Text code style={{ maxWidth: 140 }} ellipsis={{ tooltip: String(v || '') }}>
                {v || '—'}
              </Typography.Text>
              {chatRef ? (
                <Tooltip title="Открыть чат">
                  <Button size="small" icon={<MessageOutlined />} href={chatRef} target="_blank" />
                </Tooltip>
              ) : null}
            </Space>
          );
        }
      },
      {
        title: 'Клиент',
        key: 'client',
        width: 200,
        render: (_, it) => {
          const from = it.incoming_from_username
            ? `@${it.incoming_from_username}`
            : it.incoming_from_name
            ? it.incoming_from_name
            : it.incoming_from_id
            ? `id:${it.incoming_from_id}`
            : '—';
          return <Typography.Text>{from}</Typography.Text>;
        }
      },
      {
        title: <span style={{ whiteSpace: 'nowrap' }}>Сообщение</span>,
        dataIndex: 'incoming_text',
        key: 'incoming_text',
        width: 420,
        render: (v, it) => {
          const incRef = buildMsgLink(it.source_chat_id, it.incoming_message_id);
          return (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Typography.Text style={{ maxWidth: 390 }} ellipsis={{ tooltip: String(v || '—') }}>
                {v || '—'}
              </Typography.Text>
              <Space size={8}>
                {incRef ? (
                  <a href={incRef} target="_blank" rel="noreferrer">
                    <LinkOutlined /> Клиентское сообщение
                  </a>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                )}
                {it.alert_message_id ? (
                  (() => {
                    const alertRef = buildMsgLink(it.alert_chat_id, it.alert_message_id);
                    return alertRef ? (
                      <a href={alertRef} target="_blank" rel="noreferrer">
                        <PushpinOutlined /> Алерт
                      </a>
                    ) : null;
                  })()
                ) : null}
              </Space>
            </Space>
          );
        }
      },
      {
        title: 'Создано',
        key: 'incoming_ts',
        width: 170,
        render: (_, it) => (
          <Space direction="vertical" size={2}>
            <Typography.Text>{fmtAgo(it.incoming_ts)}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {fmtTs(it.incoming_ts)}
            </Typography.Text>
          </Space>
        )
      },
      {
        title: 'Действия',
        key: 'actions',
        width: 240,
        fixed: 'right',
        render: (_, it) => {
          return (
            <Space wrap size={8}>
              {/* SLA action buttons */}
              <Button
                size="small"
                onClick={async () => {
                  try {
                    await fetchJson(`/api/sla/${it.id}/mute`, { method: 'POST' });
                    message.success('SLA замьючен');
                    isAutoTickRef.current = true;
                    await load();
                  } catch (e) {
                    message.error(e?.message || 'Ошибка mute');
                  }
                }}
              >
                🔕 Mute
              </Button>

              <Button
                size="small"
                onClick={async () => {
                  try {
                    await fetchJson(`/api/sla/${it.id}/unmute`, { method: 'POST' });
                    message.success('SLA размьючен');
                    isAutoTickRef.current = true;
                    await load();
                  } catch (e) {
                    message.error(e?.message || 'Ошибка unmute');
                  }
                }}
              >
                🔔 Unmute
              </Button>

              <Button
                size="small"
                type="primary"
                onClick={async () => {
                  try {
                    await fetchJson(`/api/sla/${it.id}/close`, { method: 'POST' });
                    message.success('SLA закрыт');
                    isAutoTickRef.current = true;
                    await load();
                  } catch (e) {
                    message.error(e?.message || 'Ошибка закрытия SLA');
                  }
                }}
              >
                ✅ Закрыть
              </Button>

              <Button
                size="small"
                danger
                onClick={async () => {
                  try {
                    await fetchJson(`/api/sla/${it.id}/reset-alerts`, { method: 'POST' });
                    message.success('Алерты SLA сброшены');
                    isAutoTickRef.current = true;
                    await load();
                  } catch (e) {
                    message.error(e?.message || 'Ошибка сброса алертов');
                  }
                }}
              >
                ♻️ Сброс
              </Button>

              {/* Existing actions */}
              {it.alert_message_id ? (
                <Popconfirm
                  title="Удалить алерт?"
                  description="Удалится запись алерта в БД (сообщение в Telegram может остаться)."
                  okText="Удалить"
                  okType="danger"
                  cancelText="Отмена"
                  onConfirm={async () => {
                    try {
                      await deleteAlert(it.id);
                      message.success('Алерт удалён');
                      isAutoTickRef.current = true;
                      await load();
                    } catch (e) {
                      message.error(e?.message || 'Ошибка удаления алерта');
                    }
                  }}
                >
                  <Button size="small" danger icon={<PushpinOutlined />}>
                    Алерт
                  </Button>
                </Popconfirm>
              ) : null}

              <Popconfirm
                title={`Удалить SLA ${it.id}?`}
                description="Действие необратимо."
                okText="Удалить"
                okType="danger"
                cancelText="Отмена"
                onConfirm={async () => {
                  try {
                    await deleteOne(it.id);
                    message.success('SLA удалён');
                    setSelectedIds((prev) => prev.filter((x) => x !== it.id));
                    isAutoTickRef.current = true;
                    await load();
                  } catch (e) {
                    message.error(e?.message || 'Ошибка удаления SLA');
                  }
                }}
              >
                <Button size="small" danger icon={<DeleteOutlined />}>
                  Удалить
                </Button>
              </Popconfirm>
            </Space>
          );
        }
      }
    ];
  }, [deleteAlert, deleteOne, load]);

  const tableData = React.useMemo(() => {
    if (loading) {
      return Array.from({ length: 8 }).map((_, i) => ({
        key: `sk-${i}`,
        __skeleton: true,
        id: `…${i}`
      }));
    }
    return filteredItems.map((it) => ({ ...it, key: String(it.id) }));
  }, [filteredItems, loading]);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Card style={styles.hero} bodyStyle={{ padding: 18 }}>
            <Row gutter={[12, 12]} align="middle" justify="space-between">
              <Col flex="auto">
                <Space align="center" size={10} wrap>
                  <div style={styles.heroIcon}><ClockCircleOutlined /></div>
                  <div>
                    <Typography.Title level={3} style={{ margin: 0, lineHeight: 1.15 }}>
                      SLA
                    </Typography.Title>
                    <Typography.Text type="secondary">
                      Контроль просрочек, ссылки на сообщения и быстрые действия.
                    </Typography.Text>
                  </div>
                </Space>
              </Col>
              <Col>
                <Space wrap>
                  <Tooltip title="Обновить список">
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      loading={loading}
                      onClick={() => {
                        isAutoTickRef.current = false;
                        load();
                      }}
                    >
                      Обновить
                    </Button>
                  </Tooltip>

                  <Tooltip title="Очистка SLA/событий/seen_alerts по периоду">
                    <Button icon={<FilterOutlined />} danger onClick={() => setCleanupOpen(true)}>
                      Очистка
                    </Button>
                  </Tooltip>

                  <Tooltip title="Удалить выбранные SLA">
                    <Button
                      danger
                      disabled={selectedIds.length === 0}
                      onClick={deleteSelected}
                    >
                      Удалить выбранные
                    </Button>
                  </Tooltip>
                </Space>
              </Col>
            </Row>

            <Divider style={{ margin: '14px 0 12px' }} />

            <Row gutter={[12, 12]}>
              <Col xs={12} sm={6}>
                <Card size="small" style={styles.kpi} bodyStyle={{ padding: 12 }}>
                  <div style={styles.kpiRow}>
                    <Typography.Text type="secondary">Всего</Typography.Text>
                    <Typography.Text strong>{stats.total}</Typography.Text>
                  </div>
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" style={styles.kpi} bodyStyle={{ padding: 12 }}>
                  <div style={styles.kpiRow}>
                    <Typography.Text type="secondary">Ожидает</Typography.Text>
                    <Typography.Text strong>{stats.pending}</Typography.Text>
                  </div>
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" style={styles.kpi} bodyStyle={{ padding: 12 }}>
                  <div style={styles.kpiRow}>
                    <Typography.Text type="secondary">Просрочено</Typography.Text>
                    <Typography.Text strong>{stats.alerted}</Typography.Text>
                  </div>
                </Card>
              </Col>
              <Col xs={12} sm={6}>
                <Card size="small" style={styles.kpi} bodyStyle={{ padding: 12 }}>
                  <div style={styles.kpiRow}>
                    <Typography.Text type="secondary">В работе</Typography.Text>
                    <Typography.Text strong>{stats.in_progress}</Typography.Text>
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider style={{ margin: '12px 0 10px' }} />

            <Row gutter={[12, 12]} align="bottom">
              <Col xs={24} md={6}>
                <Typography.Text type="secondary">Статус</Typography.Text>
                <Select
                  value={status}
                  onChange={setStatus}
                  style={{ width: '100%', marginTop: 6 }}
                  options={[
                    { value: 'pending', label: 'pending — ожидает' },
                    { value: 'alerted', label: 'alerted — просрочено' },
                    { value: 'in_progress', label: 'in_progress — в работе' },
                    { value: 'answered', label: 'answered — закрыто' }
                  ]}
                />
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Typography.Text type="secondary">Звезда (опц.)</Typography.Text>
                <InputNumber
                  min={1}
                  max={10}
                  value={star === '' ? null : Number(star)}
                  placeholder="например: 2"
                  style={{ width: '100%', marginTop: 6 }}
                  onChange={(v) => setStar(v == null ? '' : String(v))}
                />
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Typography.Text type="secondary">Лимит</Typography.Text>
                <InputNumber
                  min={10}
                  max={200}
                  value={Number(limit) || 50}
                  style={{ width: '100%', marginTop: 6 }}
                  onChange={(v) => setLimit(Number(v) || 50)}
                />
              </Col>

              <Col xs={24} md={6}>
                <Typography.Text type="secondary">Поиск</Typography.Text>
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="текст / @user / chat_id / SLA_ID"
                  style={{ marginTop: 6 }}
                  allowClear
                />
              </Col>

              <Col xs={24}>
                <div style={{ marginTop: 10 }}>
                  <Space align="center" wrap>
                    <Switch checked={autoRefresh} onChange={setAutoRefresh} />
                    <Typography.Text>{autoRefresh ? 'Автообновление' : 'Автообновление выключено'}</Typography.Text>
                    <Typography.Text type="secondary">Интервал (сек)</Typography.Text>
                    <InputNumber
                      min={10}
                      max={300}
                      value={Number(refreshSec) || 30}
                      style={{ width: 120 }}
                      onChange={(v) => setRefreshSec(Number(v) || 30)}
                      disabled={!autoRefresh}
                    />
                    <Typography.Text type="secondary">
                      API: <Typography.Text code>GET /api/sla</Typography.Text>
                    </Typography.Text>
                  </Space>
                </div>
              </Col>
            </Row>
          </Card>

          {error ? <Alert type="error" showIcon message="Ошибка" description={error} /> : null}

          <Card bodyStyle={{ padding: 0 }} style={styles.tableCard}>
            <div style={styles.tableTop}>
              <Space wrap>
                <Typography.Text type="secondary">Найдено:</Typography.Text>
                <Typography.Text strong>{filteredItems.length}</Typography.Text>

                {selectedIds.length ? (
                  <Tag color="blue" style={{ borderRadius: 999, marginLeft: 8 }}>
                    Выбрано: {selectedIds.length}
                  </Tag>
                ) : null}
              </Space>

              <Space wrap>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    isAutoTickRef.current = false;
                    load();
                  }}
                  loading={loading}
                >
                  Обновить
                </Button>
              </Space>
            </div>

            <Divider style={{ margin: 0 }} />

            <div style={{ padding: 12 }}>
              {loading ? (
                <div style={{ padding: 6 }}>
                  <Skeleton active paragraph={{ rows: 6 }} />
                </div>
              ) : (filteredItems.length ? (
                <>
                <style>{`
                  .sla-table .ant-table-thead > tr > th { white-space: nowrap; }
                  .sla-table .ant-table-cell { vertical-align: top; }
                `}</style>
                <Table
                  className="sla-table"
                  tableLayout="fixed"
                  rowSelection={rowSelection}
                  columns={columns}
                  dataSource={tableData}
                  size="middle"
                  sticky
                  scroll={{ x: 1400 }}
                  pagination={{ pageSize: 25, showSizeChanger: true }}
                />
                </>
              ) : (
                <div style={{ padding: 24 }}>
                  <Empty description="Нет записей" />
                </div>
              ))}
            </div>
          </Card>

          {/* Cleanup Modal */}
          <Modal
            open={cleanupOpen}
            onCancel={() => setCleanupOpen(false)}
            onOk={runCleanup}
            okText="Очистить"
            okType="danger"
            cancelText="Отмена"
            title="Очистка алертов и событий"
            confirmLoading={loading}
            centered
          >
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
              <Alert
                type="warning"
                showIcon
                message="Будут удалены записи SLA/алертов и (опционально) события в базе. Действие необратимо."
              />

              <div>
                <Typography.Text>Удалить за последние (часы):</Typography.Text>
                <InputNumber
                  min={1}
                  max={720}
                  value={cleanupHours}
                  onChange={(v) => setCleanupHours(Number(v) || 1)}
                  style={{ marginLeft: 8, width: 120 }}
                />
              </div>

              <div>
                <Typography.Text>Статус:</Typography.Text>
                <Select
                  value={cleanupStatus}
                  onChange={setCleanupStatus}
                  style={{ marginLeft: 8, width: 220 }}
                  options={[
                    { value: 'all', label: 'Все' },
                    { value: 'pending', label: 'pending — ожидает' },
                    { value: 'alerted', label: 'alerted — просрочено' },
                    { value: 'in_progress', label: 'in_progress — в работе' },
                    { value: 'answered', label: 'answered — закрыто' }
                  ]}
                />
              </div>

              <div>
                <Typography.Text>Звезда:</Typography.Text>
                <Select
                  value={cleanupStar}
                  onChange={setCleanupStar}
                  style={{ marginLeft: 8, width: 140 }}
                  options={[
                    { value: 'all', label: 'Все' },
                    ...Array.from({ length: 10 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))
                  ]}
                />
              </div>

              <div>
                <Checkbox checked={cleanupAlsoEvents} onChange={(e) => setCleanupAlsoEvents(e.target.checked)}>
                  Также удалить события (events)
                </Checkbox>
              </div>

              <div>
                <Checkbox checked={cleanupAlsoSeenAlerts} onChange={(e) => setCleanupAlsoSeenAlerts(e.target.checked)}>
                  Также удалить seen_alerts
                </Checkbox>
              </div>

              <Typography.Text type="secondary">
                Источник: <Typography.Text code>DELETE /api/sla/cleanup</Typography.Text>
              </Typography.Text>
            </Space>
          </Modal>
        </Space>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background:
      'radial-gradient(1200px 600px at 20% 0%, rgba(41,128,185,0.14), transparent 60%), radial-gradient(900px 500px at 95% 10%, rgba(202,144,84,0.14), transparent 55%), linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.00))',
    padding: 12
  },
  container: {
    maxWidth: 1120,
    margin: '0 auto'
  },
  hero: {
    borderRadius: 18,
    border: '1px solid rgba(0,0,0,0.06)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.05)'
  },
  heroIcon: {
    width: 42,
    height: 42,
    borderRadius: 14,
    display: 'grid',
    placeItems: 'center',
    background: 'linear-gradient(135deg, rgba(41,128,185,0.22), rgba(202,144,84,0.18))',
    border: '1px solid rgba(0,0,0,0.06)',
    fontSize: 18
  },
  kpi: {
    borderRadius: 16,
    border: '1px solid rgba(0,0,0,0.06)',
    background: 'rgba(255,255,255,0.70)',
    backdropFilter: 'blur(6px)'
  },
  kpiRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  tableCard: {
    borderRadius: 18,
    border: '1px solid rgba(0,0,0,0.06)',
    overflow: 'hidden',
    background: 'rgba(255,255,255,0.75)',
    backdropFilter: 'blur(8px)'
  },
  tableTop: {
    padding: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap'
  }
};